import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AppShell } from "@/components/AppShell";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import InsuredAuthPage from "@/pages/InsuredAuthPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import InsuredDashboardPage from "@/pages/InsuredDashboardPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import SettingsPage from "@/pages/SettingsPage";
import TemplatesPage from "@/pages/TemplatesPage";
import InsuredsPage from "@/pages/InsuredsPage";
import PublicUploadPage from "@/pages/PublicUploadPage";
import BillingPage from "@/pages/BillingPage";
import AdminMetricsPage from "@/pages/AdminMetricsPage";
import PlatformAdminLoginPage from "@/pages/PlatformAdminLoginPage";
import LegalPage from "@/pages/LegalPage";
import NotFoundPage from "@/pages/NotFoundPage";
import PremiumLandingPage from "@/pages/PremiumLandingPage";
import PricingPage from "@/pages/PricingPage";
import VerificationCreditsPage from "@/pages/VerificationCreditsPage";
import VerificationOpsGuard from "@/pages/VerificationOpsGuard";
import VerificationRequestPage from "@/pages/VerificationRequestPage";
import VerificationDetailPage from "@/pages/VerificationDetailPage";
import VerificationLedgerPage from "@/pages/VerificationLedgerPage";
import ResourcesPage from "@/pages/ResourcesPage";
import ResourceGuidePage from "@/pages/ResourceGuidePage";
import OperationsPage from "@/pages/OperationsPage";
import InsuredCoverageProfile from "@/pages/InsuredCoverageProfile";
import { InsuredProfileShortcut } from "@/components/InsuredProfileShortcut";
import FaqPage from "@/pages/FaqPage";
import EnhancedPrivacyRequestPage from "@/pages/EnhancedPrivacyRequestPage";
import FinalLegalPage from "@/pages/FinalLegalPage";
import { CookieConsent } from "@/components/CookieConsent";
import { PricingShortcut } from "@/components/PricingShortcut";
import { FaqHashRedirect } from "@/components/FaqHashRedirect";
import ReviewQueuePage from "@/pages/ReviewQueuePage";
import GoogleCallbackPage from "@/pages/GoogleCallbackPage";
import GroupsPage from "@/pages/GroupsPage";
import GroupDetailPage from "@/pages/GroupDetailPage";

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="grid min-h-screen place-items-center bg-slate-50 text-sm text-slate-500" data-testid="app-session-loading">Loading your workspace…</div>;
  if (!user) return <Navigate replace to="/login" />;
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <FaqHashRedirect />
        <Routes>
          <Route element={<PremiumLandingPage />} path="/" />
          <Route element={<PricingPage />} path="/pricing" />
          <Route element={<ResourcesPage />} path="/resources" />
          <Route element={<ResourceGuidePage />} path="/resources/:slug" />
          <Route element={<ResourceGuidePage />} path="/solutions/:slug" />
          <Route element={<FinalLegalPage type="terms" />} path="/terms" />
          <Route element={<FinalLegalPage type="privacy" />} path="/privacy" />
          <Route element={<EnhancedPrivacyRequestPage />} path="/do-not-sell" />
          <Route element={<FaqPage />} path="/faq" />
          <Route element={<InsuredAuthPage mode="login" />} path="/login" />
          <Route element={<InsuredAuthPage mode="register" />} path="/register" />
          <Route element={<AcceptInvitePage />} path="/accept-invite" />
          <Route element={<ForgotPasswordPage />} path="/forgot-password" />
          <Route element={<GoogleCallbackPage />} path="/auth/google" />
          <Route element={<PublicUploadPage />} path="/upload/:token" />
          <Route element={<ProtectedRoute><InsuredDashboardPage /></ProtectedRoute>} path="/dashboard" />
          <Route element={<ProtectedRoute><InsuredsPage /></ProtectedRoute>} path="/vendors" />
          <Route element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} path="/groups" />
          <Route element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} path="/groups/:id" />
          <Route element={<ProtectedRoute><InsuredCoverageProfile /></ProtectedRoute>} path="/insureds/:id" />
          <Route element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} path="/templates" />
          <Route element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} path="/settings" />
          <Route element={<ProtectedRoute><ReviewQueuePage /></ProtectedRoute>} path="/review" />
          <Route element={<ProtectedRoute><BillingPage /></ProtectedRoute>} path="/billing" />
          <Route element={<ProtectedRoute><VerificationCreditsPage /></ProtectedRoute>} path="/verification-credits" />
          <Route element={<ProtectedRoute><VerificationRequestPage /></ProtectedRoute>} path="/verification-request" />
          <Route element={<ProtectedRoute><VerificationDetailPage /></ProtectedRoute>} path="/verification-request/:id" />
          <Route element={<ProtectedRoute><VerificationLedgerPage /></ProtectedRoute>} path="/verification-credits/ledger" />
          <Route element={<AdminMetricsPage />} path="/admin" />
          <Route element={<PlatformAdminLoginPage />} path="/platform-admin/login" />
          <Route element={<VerificationOpsGuard />} path="/admin/verifications" />
          <Route element={<OperationsPage />} path="/admin/operations" />
          <Route element={<NotFoundPage />} path="*" />
        </Routes>
        <PricingShortcut />
        <InsuredProfileShortcut />
        <CookieConsent />
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
